import { useState, useEffect, useRef, useCallback } from 'react';
import { ModelStatus, ModelStatusMap } from '@pipelet/common';

/**
 * Custom hook to fetch and auto-update model status every 30 seconds
 * @param enabled - Whether to enable status fetching
 * @returns Object containing status map and loading state
 */
export function useModelStatus(enabled: boolean = true) {
  const [statusMap, setStatusMap] = useState<ModelStatusMap>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await fetch('/api/v1/models/status');
      if (!response.ok) {
        throw new Error('Failed to fetch model status');
      }
      const data = (await response.json()) as ModelStatusMap;
      setStatusMap(data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch model status:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    // Initial fetch
    fetchStatus();

    // Set up interval for updates every 30 seconds
    intervalRef.current = setInterval(fetchStatus, 30000);

    // Cleanup on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, fetchStatus]);

  const getStatus = useCallback(
    (modelName: string): ModelStatus => {
      return statusMap[modelName] || 'unknown';
    },
    [statusMap],
  );

  return {
    statusMap,
    getStatus,
    isLoading,
    error,
    refetch: fetchStatus,
  };
}
